import type { QueryIdRequestBody } from '../types/api/data';
import { unwrapFilterValues } from './unwrapFilterValues';

/**
 * Produces a deterministic string key from `QueryIdRequestBody`, regardless of
 * whether filters arrive in wrapped (`{key: {value: x}}`) or flat (`{key: x}`) form.
 *
 * Used by `deduplicatePost` to detect when two callers (e.g. `useLoadPresets`
 * and `fetchPage`) request semantically identical data-queries despite
 * formatting the body differently.
 *
 * Normalization steps:
 *   1. Filters → `unwrapFilterValues` (flat form), sorted by key
 *   2. Order → sorted by `columnKey` for stability
 *   3. SmartSearch → falsy values (`''`, `null`, `undefined`) coerced to `null`
 */
export function createQueryFingerprint(params: QueryIdRequestBody): string {
  const normalizedFilters = unwrapFilterValues((params.filters ?? {}) as Record<string, unknown>);
  const sortedFilterEntries = Object.entries(normalizedFilters).toSorted(([a], [b]) => a.localeCompare(b));
  const sortedOrder = (params.order ?? []).toSorted((a, b) => a.columnKey.localeCompare(b.columnKey));
  return JSON.stringify({
    f: Object.fromEntries(sortedFilterEntries),
    o: sortedOrder,
    s: params.smartSearch || null
  });
}